import { proxy, subscribe, useSnapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import {
  canAccessColumn,
  isPublicColumn,
  sortColumnsByOrder,
} from '~shared/rundown/column'
import type { Column } from '~shared/rundown/column'

import { userStore } from '@/features/user/user.store'

import { columnsStoreReadonly, getColumn } from '../data/columns.store'
import { rundownPageStore } from '../rundown-page.store'

import {
  columnLayoutStoreReadonly,
  reconcileColumnOrder,
} from './column-layout'
import { columnShareLinkReadonly } from './column-share-link'

// =============== TYPES

type ColumnsDerived = {
  /**
   * Every loaded column in rundown order, including other users' private
   * columns. For outputs, whose URL names the columns to show.
   */
  sortedColumns: Column[]

  /**
   * `sortedColumns` without other users' private columns, in rundown order:
   * every column this user may see or pick, eg. in the output modal.
   */
  allowedColumns: Column[]

  /**
   * `sortedColumns` without any private column, not even the user's own, in
   * rundown order. For what is shared beyond this user, eg. the export and the
   * guest link modals.
   */
  publicColumns: Column[]

  /**
   * Every column id of the rundown in the user's local layout order. Ids only,
   * not joined with the documents, so an id whose document is private, not yet
   * loaded or malformed is kept. Use for writing a reorder back, as it replaces
   * the rundown's whole order and must not drop any id. Its length drives the
   * plan's column limit, as it matches the server's `rundown.columns` count.
   */
  layoutColumnIds: string[]

  /**
   * What the grid renders: `layoutColumnIds` joined with the columns the user
   * may see, minus the ones hidden by the user or by the share link.
   */
  visibleColumns: Column[]

  /**
   * Columns the user hid themselves, without the ones the share link hides, in
   * the same order as the grid. What the "Hidden columns" menu lists, as only
   * these can be shown again.
   */
  userHiddenColumns: Column[]
}

const initialState: ColumnsDerived = {
  sortedColumns: [],
  allowedColumns: [],
  publicColumns: [],
  layoutColumnIds: [],
  visibleColumns: [],
  userHiddenColumns: [],
}

// =============== STORE

/**
 * Derived views of columns.
 *
 * Column documents live in `columnsStore`, keyed by id and unordered. Their
 * order is an array of ids on `rundown.columns`. The list the UI renders is a
 * join of the two, computed once here instead of in each component.
 *
 * @remarks
 *   Read-only outside this module: it is recomputed from the two source stores,
 *   so any write would be overwritten by the next snapshot.
 *
 * @privateRemarks
 *
 *   ### Why is there no reset function?
 *
 *   Emptying the source stores on teardown notifies this one automatically, so
 *   a dedicated reset/clear function is not needed.
 *
 *   ### Why the derivation walks the order and looks columns up one by one:
 *
 *   Order is the authority on what belongs to the rundown, so a column document
 *   with no place in the array is not a column, and an id whose document has
 *   not arrived is simply skipped. The two Firestore snapshots are independent
 *   and arrive in either order, and `recompute` runs on both, so the join
 *   settles whichever way round they land.
 */
export const columnsDerived = proxy<ColumnsDerived>(deepClone(initialState))

/**
 * Rebuild every derived value from the source stores.
 *
 * Runs on any change to the rundown, the columns, the signed-in user, the
 * user's local layout, or the share link.
 */
function recompute() {
  const { rundown } = rundownPageStore
  const rundownColumnIds = rundown?.columns ?? []
  const layout = rundown?.id ? columnLayoutStoreReadonly[rundown.id] : undefined

  const canAccess = (column: Column) =>
    canAccessColumn(column, userStore.userId)

  const nextSortedColumns = sortColumnsByOrder(rundownColumnIds, getColumn)
  const nextLayoutColumnIds = reconcileColumnOrder(
    rundownColumnIds,
    layout?.columnOrder ?? [],
  )
  // `allowedColumns` in the user's layout order. Split below into visible and
  // hidden by the user; the rest is hidden by the share link.
  const layoutAllowedColumns = sortColumnsByOrder(
    nextLayoutColumnIds,
    getColumn,
  ).filter(canAccess)

  const userHiddenIds = new Set(layout?.hiddenColumns ?? [])
  const linkHiddenIds = new Set(columnShareLinkReadonly.hiddenColumns)

  assignIfChanged('sortedColumns', nextSortedColumns)
  assignIfChanged('allowedColumns', nextSortedColumns.filter(canAccess))
  assignIfChanged('publicColumns', nextSortedColumns.filter(isPublicColumn))
  assignIfChanged('layoutColumnIds', [...nextLayoutColumnIds])
  assignIfChanged(
    'visibleColumns',
    layoutAllowedColumns.filter(
      ({ id }) => !userHiddenIds.has(id) && !linkHiddenIds.has(id),
    ),
  )
  assignIfChanged(
    'userHiddenColumns',
    layoutAllowedColumns.filter(
      ({ id }) => userHiddenIds.has(id) && !linkHiddenIds.has(id),
    ),
  )
}

/**
 * Write `next` only if it differs from what is stored, compared with
 * `sameItems`.
 */
function assignIfChanged<K extends keyof ColumnsDerived>(
  key: K,
  next: ColumnsDerived[K],
) {
  if (sameItems(columnsDerived[key], next)) return

  columnsDerived[key] = next
}

/**
 * Compares lists item by item with `===`: ids by value, columns by identity.
 *
 * The store merges updates into the existing column in place, so a column keeps
 * its proxy identity for as long as it exists. Identity therefore answers
 * exactly the question this list cares about, whether the membership or the
 * order changed, and an edit to a column's own fields still reaches
 * subscribers, because valtio propagates a nested change up to the array
 * holding it.
 */
function sameItems(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index])
}

/**
 * Sources of the derivation.
 *
 * @privateRemarks
 *   Batched (default behaviour), like the cue derivation. The trade-off is that
 *   a synchronous read right after a mutation sees the previous list until the
 *   microtask runs.
 */
subscribe(rundownPageStore, recompute)
subscribe(columnsStoreReadonly, recompute)
subscribe(userStore, recompute)
subscribe(columnLayoutStoreReadonly, recompute)
subscribe(columnShareLinkReadonly, recompute)

// The stores may already hold data by the time this module is first imported.
recompute()

// =============== HOOKS

/**
 * Subscribe to the derived column state.
 */
export function useColumnsDerived() {
  return useSnapshot(columnsDerived)
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(columnsDerived, { name: 'columnsDerived', enabled: true })
}
// #endregion
